import { Card, Group, Stack, Switch, Title } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconSparkles } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { useState } from 'react';
import { GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT } from '@unify/fhir-model/constants';

/**
 * Lets the logged-in provider toggle Gemini/LLM-backed AI documentation opt-out
 * on their own Practitioner resource. Saves immediately on toggle.
 */
export function AiDocumentationOptOutCard({
  practitioner,
  onUpdated,
}: {
  practitioner: Practitioner;
  onUpdated: (updated: Practitioner) => void;
}): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);

  const isOptedOut =
    practitioner.extension?.some(
      (e) => e.url === GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT && e.valueBoolean === true,
    ) ?? false;

  const handleToggle = async (checked: boolean) => {
    setSaving(true);
    try {
      const filtered = (practitioner.extension ?? []).filter(
        (e) => e.url !== GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT,
      );
      const updated: Practitioner = {
        ...practitioner,
        extension: checked
          ? [...filtered, { url: GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT, valueBoolean: true }]
          : filtered,
      };
      const saved = await medplum.updateResource(updated);
      onUpdated(saved);
      medplum.invalidateSearches('Practitioner');
      notify({
        type: 'success',
        title: 'Setting saved',
        message: checked
          ? 'AI documentation assistance is now off. Manual documentation and automatic quality checks are unaffected.'
          : 'AI documentation assistance is now enabled.',
      });
    } catch {
      notify({
        type: 'error',
        title: 'Could not save setting',
        message: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group gap="xs">
          <IconSparkles size={18} color="var(--mantine-color-violet-6)" />
          <Title order={4}>AI documentation assistance</Title>
        </Group>

        <Switch
          label="Use AI documentation assistance"
          description="Allow Unify to use AI to help draft and revise clinical documentation, including AI note drafts and AI treatment plan suggestions. When this is off, AI note generation, AI note revision, and AI treatment plan suggestions are disabled for your work. Manual documentation and automatic quality checks still work."
          checked={!isOptedOut}
          disabled={saving}
          onChange={(e) => void handleToggle(!e.currentTarget.checked)}
        />
      </Stack>
    </Card>
  );
}
